import React, { useState, useEffect, useRef } from 'react';
import { Route, Switch, useRouteMatch, useHistory, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  Mic as MicIcon,
  SignalCellularConnectedNoInternet0Bar as DisconnectedIcon,
  SignalCellularConnectedNoInternet4Bar as ConnectedIcon
} from '@mui/icons-material';
import DeviceCard from './DeviceCard';
import DisplayModeSwitch from './DisplayModeSwitch';
import History from './DeviceHistory';
import { mockMicrophoneData, generateNewMockEvents } from '../libs/mockData';
import { fetchApiRequest, renderBoilerPlate, getApiHost } from './Common';
import { useSSE } from '../hooks/useSSE';

export const USE_MOCK_DATA = false; // Enabled for testing live updates

export function streamEventToDevice(event) {
  return {
    name: event.device_id,
    device_id: event.device_id,
    events: event.events,
    scores: event.scores,
    timestamp: event.timestamp,
    location: event.location,
    url: event.url,
  };
}

function mergeDevice(prevDevices, device) {
  const name = device.name || device.device_id;
  if (!name) {
    return prevDevices;
  }
  const normalized = { ...device, name };
  const idx = prevDevices.findIndex(d => d.name === name);
  if (idx >= 0) {
    const next = [...prevDevices];
    next[idx] = { ...next[idx], ...normalized };
    return next;
  }
  return [...prevDevices, normalized];
}

function LiveDashboard({ displayMode = 'circular' }) {
  const theme = useTheme();
  const componentId = useRef(`LiveDashboard-${Date.now()}`);

  // Use SSE hook for live updates
  const { data, status: connectionStatus, error } = useSSE('/v1/events/stream', {
    fallbackToPolling: true,
    pollInterval: 10000, // 10 seconds
  });



  // Handle mock data
  useEffect(() => {
    if (USE_MOCK_DATA) {
      setTimeout(() => {
        setDevices(mockMicrophoneData);
      }, 500);
    }
  }, []);

  const [devices, setDevices] = useState([]);
  const [initialLoadDone, setInitialLoadDone] = useState(USE_MOCK_DATA);

  // Load registered devices and their last event on mount
  useEffect(() => {
    if (USE_MOCK_DATA) {
      return;
    }

    getApiHost((apiHost) => {
      fetchApiRequest(
        `${apiHost}/v1/devices?last_event=true`,
        (fetchPromise) => {
          fetchPromise
            .then((res) => {
              if (!res.ok) {
                throw new Error('Failed to fetch devices');
              }
              return res.json();
            })
            .then((response) => {
              if (response?.devices) {
                setDevices(response.devices.map((d) => ({ ...d, name: d.name })));
              }
              setInitialLoadDone(true);
            })
            .catch((err) => {
              console.error('Error loading devices:', err);
              setInitialLoadDone(true);
            });
        },
      );
    });
  }, []);

  // Merge live SSE events or polling fallback snapshot
  useEffect(() => {
    if (!data) {
      return;
    }

    if (data.devices) {
      setDevices(data.devices.map((d) => ({ ...d, name: d.name })));
      return;
    }

    if (data.device_id) {
      setDevices((prevDevices) => (
        mergeDevice(prevDevices, streamEventToDevice(data))
      ));
    }
  }, [data]);

  // Mock live updates for testing - generates new device events every 12 seconds
  useEffect(() => {
    if (USE_MOCK_DATA) {
      const mockInterval = setInterval(() => {
        const mockStart = performance.now();
        const newEvent = generateNewMockEvents();

        setDevices(prevDevices => {
          const updateStart = performance.now();
          // Update existing device or add new one
          const existingIndex = prevDevices.findIndex(d => d.name === newEvent.name);
          if (existingIndex >= 0) {
            // Update existing device with new data
            const updatedDevices = [...prevDevices];
            updatedDevices[existingIndex] = {
              ...updatedDevices[existingIndex],
              ...newEvent,
              timestamp: newEvent.timestamp
            };
            const updateDuration = performance.now() - updateStart;
            return updatedDevices;
          } else {
            // Add new device to the list
            const updateDuration = performance.now() - updateStart;
            return [...prevDevices, newEvent];
          }
        });

        const mockTotalDuration = performance.now() - mockStart;
      }, 8000); // Update devices every 8 seconds for faster testing

      return () => {
        clearInterval(mockInterval);
      };
    }
  }, [USE_MOCK_DATA]);

  return (
    <Paper>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="subtitle1">Registered Devices</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            icon={connectionStatus.connected ? <ConnectedIcon /> : <DisconnectedIcon />}
            label={connectionStatus.text}
            color={connectionStatus.connected ? "success" : "error"}
            variant="outlined"
            sx={{ paddingLeft: '0.4rem' }}
            onClick={(e) => e.preventDefault()}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Connection Error: {error}
        </Alert>
      )}

      {!initialLoadDone ? (
        <Box textAlign="center" mt={4}>
          <CircularProgress color="primary" />
          <Typography
            variant="h6"
            color="textSecondary"
            mt={2}
            sx={{ color: theme.palette.text.secondary }}
          >
            Loading devices...
          </Typography>
        </Box>
      ) : devices.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '8px', // Both vertical and horizontal gaps set to 8px
            maxWidth: 'calc(3 * 320px + 2 * 8px)', // Updated for 8px horizontal gaps
            width: '100%',
            gridAutoRows: '1fr' // Each row takes equal height
          }}>
            {devices.map((device, index) => (
              <Box
                key={device.name || index}
                component={Link}
                to={`/devices/${encodeURIComponent(device.name)}`}
                sx={{
                  cursor: 'pointer',
                  minHeight: '280px',
                  display: 'flex',
                  maxWidth: '320px',
                  width: '100%',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'none'
                  }
                }}
              >
                <DeviceCard mic={device} isLink={false} displayMode={displayMode} />
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box textAlign="center" mt={4}>
          <CircularProgress color="primary" />
          <Typography
            variant="h6"
            color="textSecondary"
            mt={2}
            sx={{ color: theme.palette.text.secondary }}
          >
            Waiting for audio events...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function Devices() {
  const theme = useTheme();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const [displayMode, setDisplayMode] = useState('circular');

  return renderBoilerPlate(
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <DisplayModeSwitch
          value={displayMode}
          onChange={setDisplayMode}
        />
        <LiveDashboard displayMode={displayMode} />
      </div>

      <div>
        <Paper sx={{ maxWidth: 'calc(3 * 320px + 2 * 8px)', width: '100%' }}>
          <Typography variant="subtitle1">Prediction History</Typography>
          <Box sx={{ maxWidth: 'calc(3 * 320px + 2 * 8px)', width: '100%' }}>
            <History displayMode={displayMode} />
          </Box>
        </Paper>
      </div>
    </div>
  );
}

export default Devices;